import {
  Activity as OctopusActivity,
  ActivityFieldOptionsResponse,
} from '../models/activity-manager';
import {
  ActivityManagerFieldOption,
  ColumnWithTranslation,
  Activity,
} from '../../../models';
import { ConfigurationMapper } from './configuration';
import { Mapper } from '../../../mappers';
import { Octopus } from '../..';
import { RecurrenceType } from '../models/recurrence-type';

export class ActivityFieldOptionMapper extends ConfigurationMapper<
  ActivityFieldOptionsResponse,
  ActivityManagerFieldOption
> {
  from(source: ActivityFieldOptionsResponse): ActivityManagerFieldOption {
    const activity = source.Activity?.FieldOptions;

    return {
      types: activity?.Type?.map(this.mapValueAndDisplay) ?? [],
    };
  }
}
export abstract class ActivityMapper<
  Source extends OctopusActivity,
  Target extends Activity,
> extends Mapper<Source[], Target[]> {
  constructor(private readonly columns?: ColumnWithTranslation[]) {
    super();
  }

  override from(source: Source[]): Target[] {
    return source.map((item) => {
      return this.mapItem(item);
    });
  }
  mapItem(source: Source): Target {
    const activity: Activity = {
      key: source.Key,
      type: source.Type,
      secStatus: source.SecStatus,
      completed: source.Completed,
      creator: source.Creator?.Value ?? '',
      userKeys: source.Users.map((user) => user.Key),
      startDate: source.StartDate?.Value ?? '',
      endDate: source.EndDate?.Value ?? '',
      completionDate: source.CompletionDate?.Value ?? '',
      abEntries: source.AbEntries,
      leads: source.Leads,
      withKey: source.WithKey,
      recurrence: source.RecurrenceKey,
      isActionPlan: source.ActionPlanKey !== null,
      isWorkflowActivity: source.WorkflowInstanceKey !== null,
      isLastActivity:
        source.RecurrenceType !== null &&
        source.RecurrenceType === RecurrenceType.Master,
    };
    if (source.With) {
      if (source.With?.Opportunity) {
        activity.regarding = {
          opportunity: source.With.Opportunity,
        };
      } else if (source.With?.Case) {
        activity.regarding = {
          case: source.With.Case,
        };
      }
    }
    if (this.columns) {
      this.columns.forEach((column: ColumnWithTranslation) => {
        const id = column.id;
        const fieldid = this.getField(column);
        const isMultipleEntries =
          activity.abEntries?.length > 1 || activity.leads?.length > 1;
        const clickable = this.detectClickable(column, isMultipleEntries);
        const columnitem = source[id] as Octopus.ColumItem;
        if (
          column.field.toLowerCase().indexOf('completiondate') !== -1 ||
          column.field.toLowerCase().indexOf('startdate') !== -1 ||
          column.field.toLowerCase().indexOf('enddate') !== -1
        ) {
          activity[fieldid + 'DisplayValue'] = columnitem.Value.trim();
        } else if (
          column.field.toLowerCase().indexOf('subject') !== -1 ||
          column.field.toLowerCase().indexOf('description') !== -1
        ) {
          activity[fieldid] =
            columnitem.Value.trim().length > 200
              ? columnitem.Value.trim().substring(0, 200) + '...'
              : columnitem.Value.trim();
        } else if (fieldid !== 'completed') {
          activity[fieldid] = columnitem.Value.trim();
        }

        if (
          !activity.secStatus.CanRead &&
          activity[fieldid] === '' &&
          !activity['private']
        ) {
          activity['private'] = {
            columnid: fieldid,
          };
        }
        activity['c.' + fieldid] = {
          type: this.getEntryType(column, source),
          clickable: clickable,
        };
      });
    }
    return activity as Target;
  }
  getField(column: ColumnWithTranslation): string {
    if (
      column.field.toLowerCase().indexOf('leads') !== -1 ||
      column.field.toLowerCase().indexOf('abentries') !== -1
    ) {
      return 'entry';
    }
    return column.field.replace('{{', '').replace('}}', '');
  }
  getEntryType(column: ColumnWithTranslation, source: Source): string {
    if (
      column.field.toLowerCase().indexOf('leads') !== -1 ||
      column.field.toLowerCase().indexOf('abentries') !== -1
    ) {
      if (source.Leads) {
        return 'lead';
      } else if (source.AbEntries) {
        return 'addressBook';
      }
    } else if (column.field.toLowerCase().indexOf('with') !== -1) {
      if (source.With?.Opportunity) {
        return 'opportunity';
      } else if (source.With?.Case) {
        return 'case';
      }
    }
    return '';
  }
  detectClickable(
    column: ColumnWithTranslation,
    isMultipleEntries: boolean,
  ): boolean {
    if (column.field.toLowerCase().indexOf('subject') !== -1) {
      return true;
    } else if (
      column.field.toLowerCase().indexOf('leads') !== -1 &&
      column.field.toLowerCase().indexOf('abentries') !== -1
    ) {
      if (isMultipleEntries) {
        return false;
      }
      return true;
    } else if (column.field.toLowerCase().indexOf('with') !== -1) {
      return true;
    }
    return false;
  }
}
export class ActivityManagerMapper extends ActivityMapper<
  OctopusActivity,
  Activity
> {
  override mapItem(source: OctopusActivity): Activity {
    const activity = super.mapItem(source);
    return activity;
  }
}
